// Pinia auth store. Two modes coexist:
//
//   permissive (default in dev): tenantId hint sent as x-lymon-tenant-id.
//     User picks the tenant in the top bar; the gateway runs with
//     LYMON_AUTH_REQUIRED=false and trusts the header.
//
//   oidc (staging / prod): MSAL acquires a Bearer token; tenantId comes
//     from the id_token claims (extension_lymon_tenant_id or tid).
//     Pinia just holds the latest session + refreshes when expiry nears.
//
// Mode toggle: VITE_AUTH_MODE env var consumed at build time.

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  bootstrap as oidcBootstrap,
  isOidcEnabled,
  refreshToken,
  signIn as oidcSignIn,
  signOut as oidcSignOut,
  type UserSession,
} from '../auth/oidc';

const TENANT_LS_KEY = 'lymon.tenantId';
const USER_SUB_LS_KEY = 'lymon.userSub';
// OIDC workspace switcher — the tenant the user picked among the ones they
// belong to. Sent as x-lymon-tenant-id; the gateway honours it only after
// confirming membership (else it falls back to the user's default tenant).
const ACTIVE_TENANT_LS_KEY = 'lymon.activeTenantId';

export interface TenantMembership {
  tenant_id: string;
  name: string | null;
  role: 'owner' | 'member' | string;
  is_active: boolean;
  /** Trial ended + free tier off → workspace locked (ADR 57). */
  locked?: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  // ── Mode ────────────────────────────────────────────────────────────
  const mode = ref<'permissive' | 'oidc' | 'operator'>(isOidcEnabled() ? 'oidc' : 'permissive');
  type OperatorSession = {access_token:string;expires_at_ms:number;tenant_id:string;user_sub:string;name:string};
  const operatorSession = ref<OperatorSession|null>(null);
  const initializationFailed = ref(false);
  async function initialize() {
    bootstrapping.value=true;initializationFailed.value=false;
    try {
      const r=await fetch('/api/auth/options',{cache:'no-store'});
      if(!r.ok)throw Error('Authentication service unavailable');
      const options=await r.json();
      if(typeof options.operator!=='boolean'||typeof options.required!=='boolean')throw Error('Invalid authentication configuration');
      if(options.operator) {
        mode.value='operator';
        const saved=sessionStorage.getItem('lymon.operator');
        if(saved) {const s=JSON.parse(saved) as OperatorSession;if(s.expires_at_ms>Date.now())operatorSession.value=s;}
      } else if(options.required && mode.value==='permissive')throw Error('Interactive authentication is not configured');
    } catch { operatorSession.value=null;initializationFailed.value=true; }
    finally {bootstrapping.value=false;}
  }
  async function operatorLogin(tenant_id:string,username:string,password:string) {
    const r=await fetch('/api/operator/login',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({tenant_id,username,password})});
    if(!r.ok)throw Error('operator_login_failed');
    operatorSession.value=await r.json();sessionStorage.setItem('lymon.operator',JSON.stringify(operatorSession.value));
    await refreshWhoami();
  }

  // ── Permissive mode state ───────────────────────────────────────────
  const manualTenantId = ref<string>(localStorage.getItem(TENANT_LS_KEY) ?? 'default');
  // Sprint 10 — let the dev identify as a specific user so invitation
  // accept, dashboard favorites and (future) RBAC tests behave as in
  // prod. Default to 'spike-anonymous' which preserves the original
  // pre-multi-user dev behaviour.
  const manualUserSub = ref<string>(
    localStorage.getItem(USER_SUB_LS_KEY) ?? 'spike-anonymous',
  );

  // ── OIDC mode state ─────────────────────────────────────────────────
  const session = ref<UserSession | null>(null);
  const bootstrapping = ref(true);
  const error = ref<string | null>(null);

  // ── Workspace switcher (OIDC) ───────────────────────────────────────
  // activeTenantId is the user's chosen workspace; null until whoami pins
  // the resolved default on first load. tenants is the membership list
  // shown in the top-bar picker (loaded from /api/me/tenants).
  const activeTenantId = ref<string | null>(
    localStorage.getItem(ACTIVE_TENANT_LS_KEY) || null,
  );
  const tenants = ref<TenantMembership[]>([]);

  // ── Whoami (Sprint 8) — role + admin gate. Set after the gateway
  //    responds to GET /api/whoami, which both auth modes hit on
  //    bootstrap. Default false so the ops sidebar stays hidden
  //    until we have the server's word.
  const isAdmin = ref(false);
  // Sprint 11 — workspace owner flag, populated by /api/whoami. Owners
  // can invite teammates, manage LLM config, change tenant settings.
  // Admins (Lybits ops) always evaluate as owner via the server-side
  // is_owner gate; the UI mirrors that so the same components work for
  // both roles.
  const isOwner = ref(false);
  // Friendly workspace name from /api/whoami (app bar). Null = show the id.
  const tenantName = ref<string | null>(null);
  // ── Signup state (Sprint 9). True when the user is OIDC-authed but
  //    has no tenant binding yet — the gateway returns 403 tenant_required
  //    from /api/whoami in that case. App.vue routes them to Signup.vue.
  const needsSignup = ref(false);
  // ── Lifecycle (ADR 57) — the workspace trial ended and the free tier is
  //    disabled, so the gateway locked it (402 subscription_required on the
  //    data plane). App.vue shows an upgrade wall. purgeAfter is the deletion
  //    deadline shown to the owner.
  const workspaceLocked = ref(false);
  const purgeAfter = ref<string | null>(null);

  // ── Derived ─────────────────────────────────────────────────────────
  // The active tenant id sent on every request as x-lymon-tenant-id. In
  // OIDC mode the chosen workspace wins over the JWT-derived id (which for
  // CIAM is the shared directory guid, not a real Lymon tenant); the
  // gateway validates membership before honouring it. All existing header
  // builders read this computed, so the switcher is wired everywhere by
  // changing it here alone.
  const tenantId = computed(() =>
    mode.value === 'operator' ? operatorSession.value?.tenant_id ?? null : mode.value === 'oidc'
      ? (activeTenantId.value ?? session.value?.tenantId ?? null)
      : manualTenantId.value,
  );
  const bearer = computed(() =>
    mode.value === 'operator' ? operatorSession.value?.access_token ?? null : mode.value === 'oidc' ? (session.value?.accessToken ?? null) : null,
  );
  const userDisplayName = computed(() => {
    if(mode.value==='operator')return operatorSession.value?.name ?? null;
    if (mode.value !== 'oidc' || !session.value) return null;
    const a = session.value.account;
    return a.name ?? a.username ?? null;
  });
  const isAuthenticated = computed(() =>
    !initializationFailed.value && (mode.value === 'operator' ? operatorSession.value !== null : mode.value === 'oidc' ? session.value !== null : true),
  );

  async function refreshWhoami(): Promise<void> {
    try {
      const r = await fetch('/api/whoami', {
        headers: buildHeaders(),
      });
      if (r.status === 403) {
        // tenant_required — the user is authenticated but hasn't been
        // bound to a tenant yet. Route them to Signup.
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        needsSignup.value = body.error === 'tenant_required';
        isAdmin.value = false;
        isOwner.value = false;
        tenantName.value = null;
        workspaceLocked.value = false;
        purgeAfter.value = null;
        return;
      }
      if (!r.ok) {
        isAdmin.value = false;
        isOwner.value = false;
        tenantName.value = null;
        needsSignup.value = false;
        workspaceLocked.value = false;
        purgeAfter.value = null;
        return;
      }
      const body = (await r.json()) as {
        is_admin?: boolean;
        is_owner?: boolean;
        tenant_name?: string | null;
        tenant_id?: string | null;
        lifecycle?: { status: string; locked: boolean; purge_after: string | null } | null;
      };
      isAdmin.value = body.is_admin === true;
      isOwner.value = body.is_owner === true;
      tenantName.value = body.tenant_name ?? null;
      needsSignup.value = false;
      workspaceLocked.value = body.lifecycle?.locked === true;
      purgeAfter.value = body.lifecycle?.purge_after ?? null;
      // Pin the resolved workspace as the active one on first load (when
      // the user hasn't picked yet). Subsequent requests then carry the
      // real Lymon tenant id rather than the CIAM directory guid.
      if (mode.value === 'oidc' && !activeTenantId.value && body.tenant_id) {
        activeTenantId.value = body.tenant_id;
        localStorage.setItem(ACTIVE_TENANT_LS_KEY, body.tenant_id);
      }
    } catch {
      isAdmin.value = false;
      isOwner.value = false;
      tenantName.value = null;
      needsSignup.value = false;
      workspaceLocked.value = false;
      purgeAfter.value = null;
    }
  }

  async function signup(workspaceName: string | null): Promise<void> {
    const r = await fetch('/api/signup', {
      method: 'POST',
      headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspace_name: workspaceName ?? '' }),
    });
    if (!r.ok) {
      const body = (await r.json().catch(() => ({}))) as { detail?: string };
      throw new Error(body.detail ?? `signup failed: ${r.status}`);
    }
    // After provisioning, the next /api/whoami will resolve the tenant
    // via tenant_users — kick a refresh to flip needsSignup off.
    await refreshWhoami();
  }

  // Load the user's workspace memberships for the top-bar switcher.
  // OIDC-only — permissive dev mode uses the manual tenant input instead.
  async function loadTenants(): Promise<void> {
    if (mode.value !== 'oidc') return;
    try {
      const r = await fetch('/api/me/tenants', { headers: buildHeaders() });
      if (!r.ok) {
        tenants.value = [];
        return;
      }
      const body = (await r.json()) as {
        tenants?: TenantMembership[];
        active_tenant_id?: string | null;
      };
      tenants.value = body.tenants ?? [];
      // Keep the pin in sync with the server's resolved tenant.
      if (!activeTenantId.value && body.active_tenant_id) {
        activeTenantId.value = body.active_tenant_id;
        localStorage.setItem(ACTIVE_TENANT_LS_KEY, body.active_tenant_id);
      }
    } catch {
      tenants.value = [];
    }
  }

  // Switch the active workspace. Persists the choice, re-reads whoami so
  // role/name reflect the new tenant, then hard-reloads so every page
  // re-fetches its tenant-scoped data from scratch (favorites, designs,
  // members, …). No-op when selecting the already-active tenant.
  async function switchTenant(id: string): Promise<void> {
    if (mode.value !== 'oidc' || !id || id === activeTenantId.value) return;
    activeTenantId.value = id;
    localStorage.setItem(ACTIVE_TENANT_LS_KEY, id);
    await refreshWhoami();
    window.location.assign('/');
  }

  function buildHeaders(): Record<string, string> {
    const h: Record<string, string> = {};
    const t = bearer.value;
    if (t) h.Authorization = `Bearer ${t}`;
    // Use the SAME resolved tenant the axios client sends (tenantId
    // computed): in OIDC that's the active workspace selection, not the
    // raw CIAM directory guid. Without this, whoami/me-tenants fetched here
    // would keep reporting the old (default) workspace after a switch, so
    // the picker never appeared to change.
    const tid = tenantId.value;
    if (tid) h['x-lymon-tenant-id'] = tid;
    // Permissive-mode user_sub header. In OIDC mode the gateway pulls
    // sub from the JWT and ignores this header. We always pass it in
    // permissive so multi-user features (invitation accept, favorites)
    // behave correctly without a real identity provider.
    if (mode.value === 'permissive' && manualUserSub.value) {
      h['x-lymon-user-sub'] = manualUserSub.value;
    }
    return h;
  }

  // ── Actions ─────────────────────────────────────────────────────────
  function setManualTenantId(id: string): void {
    manualTenantId.value = id;
    if (id) localStorage.setItem(TENANT_LS_KEY, id);
    else localStorage.removeItem(TENANT_LS_KEY);
  }

  function setManualUserSub(sub: string): void {
    // Same sanitiser as the gateway side: [a-zA-Z0-9_-]+ up to 64 chars.
    // Reject silently — the input has its own UI hint.
    if (sub && !/^[a-zA-Z0-9_-]{1,64}$/.test(sub)) return;
    manualUserSub.value = sub || 'spike-anonymous';
    if (sub) localStorage.setItem(USER_SUB_LS_KEY, sub);
    else localStorage.removeItem(USER_SUB_LS_KEY);
  }

  async function bootstrap(): Promise<void> {
    if (mode.value !== 'oidc') return;
    bootstrapping.value = true;
    error.value = null;
    try {
      session.value = await oidcBootstrap();
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
      session.value = null;
    } finally {
      bootstrapping.value = false;
    }
  }

  // Shared inflight promise so concurrent callers (every API call checks
  // expiry) coalesce into ONE refreshToken() instead of racing MSAL.
  let refreshInflight: Promise<void> | null = null;
  async function refreshIfNeeded(): Promise<void> {
    if(mode.value==='operator' && operatorSession.value && operatorSession.value.expires_at_ms<=Date.now()) {await logout();return;}
    if (mode.value !== 'oidc' || !session.value) return;
    // Refresh when ≤ 2 minutes remain — handles long-running dashboards.
    if (session.value.expiresAtMs - Date.now() >= 2 * 60 * 1000) return;
    if (refreshInflight) return refreshInflight;
    refreshInflight = (async () => {
      try {
        const next = await refreshToken();
        if (next) session.value = next;
      } catch {
        // Silent fail — next API call will hit 401 and the axios
        // interceptor will force a fresh bootstrap.
      } finally {
        refreshInflight = null;
      }
    })();
    return refreshInflight;
  }

  async function login(): Promise<void> {
    if (mode.value !== 'oidc') return;
    await oidcSignIn();
  }

  async function logout(): Promise<void> {
    if(mode.value==='operator') {operatorSession.value=null;sessionStorage.removeItem('lymon.operator');isOwner.value=false;isAdmin.value=false;return;}
    if (mode.value === 'oidc') {
      activeTenantId.value = null;
      tenants.value = [];
      localStorage.removeItem(ACTIVE_TENANT_LS_KEY);
      await oidcSignOut();
      session.value = null;
    } else {
      setManualTenantId('default');
    }
  }

  return {
    initializationFailed,
    initialize,
    operatorLogin,
    mode,
    manualTenantId,
    setManualTenantId,
    manualUserSub,
    setManualUserSub,
    session,
    bootstrapping,
    error,
    bootstrap,
    refreshIfNeeded,
    login,
    logout,
    tenantId,
    bearer,
    userDisplayName,
    isAuthenticated,
    isAdmin,
    isOwner,
    tenantName,
    needsSignup,
    workspaceLocked,
    purgeAfter,
    refreshWhoami,
    signup,
    activeTenantId,
    tenants,
    loadTenants,
    switchTenant,
  };
});
